import type { AdminAppealDetailDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';
import { formatCurrency } from '../../../utils/format';

/**
 * Read-only persisted-decision block for a RESOLVED appeal (AUT-130,
 * architect 🟠 #4). When `status === 'resolved'` the drawer renders this
 * instead of action buttons — it surfaces the decision fields the detail
 * wire already carries:
 *   - decisionOutcome (translated)
 *   - decisionReason (customer-visible justification)
 *   - refundAmount / refundPercentage (for the refund outcomes)
 *   - inspectorPenaltyType / Days / Reason (for inspector_penalty)
 *   - adminNotes (internal commentary)
 *
 * Each field renders only when present (null fields are skipped) so a
 * `reject_appeal` resolution (no money / no penalty) shows just the
 * outcome + reason.
 */

const Row = ({ label, children }: { label: string; children: ReactNode }): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
    <Typography variant="caption" sx={{ color: tokens.text.tertiary }}>
      {label}
    </Typography>
    <Box sx={{ fontSize: 14 }}>{children}</Box>
  </Box>
);

/** Refund amount / percentage rows (rendered for the refund outcomes). */
const RefundRows = ({ detail }: { detail: AdminAppealDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const { refundAmount, refundPercentage } = detail;
  return (
    <>
      {refundAmount !== null ? (
        <Row label={t('appeals.resolveDialog.refundAmountLabel')}>
          <Typography variant="body2" fontFamily="monospace">
            {formatCurrency(Number(refundAmount))}
          </Typography>
        </Row>
      ) : null}

      {refundPercentage !== null ? (
        <Row label={t('appeals.resolveDialog.refundPercentageLabel')}>
          <Typography variant="body2" fontFamily="monospace">{`${refundPercentage}%`}</Typography>
        </Row>
      ) : null}
    </>
  );
};

/** Penalty type (+ days) / penalty reason rows (rendered for inspector_penalty). */
const PenaltyRows = ({ detail }: { detail: AdminAppealDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const { inspectorPenaltyType, inspectorPenaltyDays, inspectorPenaltyReason } = detail;
  return (
    <>
      {inspectorPenaltyType ? (
        <Row label={t('appeals.resolveDialog.penaltyTypeLabel')}>
          <Typography variant="body2">
            {t(`appeals.resolveDialog.penaltyType.${inspectorPenaltyType}`)}
            {inspectorPenaltyDays !== null
              ? ` · ${t('appeals.resolveDialog.daysValue', { count: inspectorPenaltyDays })}`
              : ''}
          </Typography>
        </Row>
      ) : null}

      {inspectorPenaltyReason ? (
        <Row label={t('appeals.detail.fields.penaltyReason')}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {inspectorPenaltyReason}
          </Typography>
        </Row>
      ) : null}
    </>
  );
};

/** Decision reason (customer-visible) + admin notes (internal) text rows. */
const NotesRows = ({ detail }: { detail: AdminAppealDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const { decisionReason, adminNotes } = detail;
  return (
    <>
      {decisionReason ? (
        <Row label={t('appeals.resolveDialog.decisionReasonLabel')}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {decisionReason}
          </Typography>
        </Row>
      ) : null}

      {adminNotes ? (
        <Row label={t('appeals.resolveDialog.adminNotesLabel')}>
          <Typography variant="body2" sx={{ color: tokens.text.secondary, whiteSpace: 'pre-wrap' }}>
            {adminNotes}
          </Typography>
        </Row>
      ) : null}
    </>
  );
};

export const ResolvedDecisionBlock = ({
  detail,
}: {
  detail: AdminAppealDetailDto;
}): ReactElement => {
  const { t } = useTranslation();
  const { decisionOutcome } = detail;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          fontWeight: 600,
          color: tokens.text.tertiary,
        }}
      >
        {t('appeals.detail.decisionTitle')}
      </Typography>

      {decisionOutcome ? (
        <Row label={t('appeals.resolveDialog.outcomeLabel')}>
          <Typography variant="body2" fontWeight={600}>
            {t(`appeals.resolveDialog.outcome.${decisionOutcome}`)}
          </Typography>
        </Row>
      ) : null}

      <RefundRows detail={detail} />
      <PenaltyRows detail={detail} />
      <NotesRows detail={detail} />
    </Box>
  );
};
